import {
  Box,
  Typography,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import StatusChip from "../../components/StatusChip";
import CompactPreview from "../../components/CompactPreview";

import { tokens } from "../../theme/tokens";
import { approvalStatusTone } from "../../status/StatusChipTones";

import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";
import { useSOPApprovals } from "../../hooks/useSOPApprovals";

const approvalStatusLabel = (status) => {
  const labels = {
    PENDING: "Pending approval",
    PENDING_APPROVAL: "Pending approval",
    APPROVED: "Approved",
    REJECTED: "Rejected",
  };

  return labels[status] || "Pending approval";
};

export default function TenantStaffSOPApprovalsView() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const {
    user,
    tenantId,
    tenantName,
  } = useResolvedTenantId();

  const loggedInUserId = user?.id;

  const {
    myApprovalSOPs,
    loading,
    error,
  } = useSOPApprovals({
    tenantId,
    userId: loggedInUserId,
  });

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="SOPS TO APPROVE"
          subtitle="Tenant could not be resolved"
        />

        <Typography mt={3}>
          A tenant must be available before viewing SOP
          approvals.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        mb={2}
        gap={1}
        flexWrap="wrap"
      >
        <Header
          title="SOPS TO APPROVE"
          subtitle={
            tenantName
              ? `SOPs awaiting your approval for ${tenantName}`
              : "SOPs awaiting your approval"
          }
        />
      </Box>

      {loading ? (
        <Typography>
          Loading SOP approvals...
        </Typography>
      ) : error ? (
        <Typography color="error">
          Unable to load SOP approvals.
        </Typography>
      ) : myApprovalSOPs.length === 0 ? (
        <Typography>
          You have no SOPs waiting for approval.
        </Typography>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
        >
          {myApprovalSOPs.map((sop) => {
            const approvalStatus =
              sop.approvalStatus || "PENDING_APPROVAL";

            return (
              <Paper
                key={sop.id}
                sx={{
                  p: 2,
                  border: `1px solid ${colors.scalpelTeal[400]}`,
                  bgcolor: "background.paper",
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <StatusChip
                    label={approvalStatusLabel(
                      approvalStatus
                    )}
                    tone={approvalStatusTone(
                      approvalStatus
                    )}
                  />
                </Box>

                <Typography
                  variant="h4"
                  sx={{ mb: 0.5 }}
                >
                  {sop.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
              <CompactPreview lines={8}>
                {sop.description || "No description"}
              </CompactPreview>
                </Typography>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={1}
                  gap={1}
                  flexWrap="wrap"
                >
                  <ActionButton
                    tone="success"
                    onClick={() =>
                      navigate(`/sops/read/${sop.id}`, {
                        state: {
                          redirectPath: "/my-sop-approvals",
                        },
                      })
                    }
                  >
                    Read SOP
                  </ActionButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}